import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  formatAmount,
  formatAmountValue,
  fractionToPercent,
  isoDateTimeToLabel,
  pluralize,
  sqlDateToLabel,
} from 'helpers/helpers';

import { typeIcon } from 'pages/Accounts/Edit/components/iconHelper';
import { altColor, dateColor, failedDepositColor } from './Chart'; // targetColor, depositColor, reserveColor, dateColor,
import { DepositBySubtype, EventLabelProps, EventType } from './model'; // targetColor, depositColor, reserveColor, dateColor,
import { TargetFrequency } from 'stores/workflowStores/finances/targets/model';
import { CalculationMode } from 'common/interfaces';
import { PersonType } from 'enums';
import useStyles from './styles';
import { ExtProductUsage } from 'pages/Accounts/Edit/components/ExternalProducts/model';

const EventLabel: React.FC<EventLabelProps> = ({ rentData, ...props }) => {
  const { t } = useTranslation();
  const classes = useStyles(props);

  return (
    <span
      className={classes.label}
      data-event-id={props.id}
      data-event-type={props.rent ? 'PORTFOLIO_RENT' : props.type}
      data-ext={props.extProduct ? 'true' : 'false'}
      data-total={props.totalAtPos}
      data-size={props.size}
      data-pos={props.pos}
      data-dev={!!props.historical}
      data-zeroed={props.type === 'LOAN'}
      data-positioned="false"
    >
      <em />
      <em />
      <strong>
        {props.icon ? (
          <img src={`data:image/png;base64,${props.icon}`} alt="" />
        ) : (
          !props.historical &&
          props.type !== 'LOAN' &&
          typeIcon(props.iconType, props.altColors ? altColor : props.type === 'DATE' ? dateColor : undefined)
        )}
      </strong>
      {props.historical && (
        <>
          <span>
            <strong style={{ fontSize: '14px' }}>{props.date && isoDateTimeToLabel(props.date, 'LLLL yyyy')}</strong>:
            <br />
            {props.title && (
              <>
                {t('accountDetail_graph_label_targetTitle')}: <strong>{props.title}</strong>
                <br />
              </>
            )}
            {props.type === EventType.PERIODIC_DEPOSIT && (
              <>
                {t('accountDetail_graph_label_pDepositTitle')}
                <br />
              </>
            )}
            {props.failedDeposit === true && (
              <>
                <span style={{ color: failedDepositColor }}>{t('accountDetail_movements_type_FAILED_DEPOSIT')}</span>
                <br />
              </>
            )}
            {t(`accountDetail_graph_label_${props.type}`)}:{' '}
            <strong>{props.value && formatAmount(props.value, props.currency)}</strong>
            {props.subtype && (
              <>
                {' '}
                (
                {String(
                  t(props.subtype === DepositBySubtype.CASH_DEPOSIT ? 'general_client' : 'general_employer'),
                ).toLocaleLowerCase()}
                )
              </>
            )}
          </span>
        </>
      )}

      {!props.pdf && !props.historical && (
        <span>
          {props.type === 'LOAN' ? (
            <>
              <strong>
                {props.targetTitle}
                <br />({props.title})
              </strong>
              <br />
              {t('externalProduct_loanUntil')}:{' '}
              <strong>{props.until && isoDateTimeToLabel(props.until, 'LLLL yyyy')}</strong>
              <br />
              {props.usage !== ExtProductUsage.NONE && <>{t('externalProduct_value')}{' '}</>}
              <strong>{props.value && formatAmountValue(props.value.value, props.currency)}</strong>{' '}
              {t('general_frequency_MONTHLY')}
            </>
          ) : props.type === 'DATE' ? (
            <strong>
              {props.title} ({props.date && isoDateTimeToLabel(props.date, 'LLLL yyyy')}):
              <br />
              {props.value && formatAmountValue(props.value.value, props.currency)}
            </strong>
          ) : (
            <>
              {props.date ? (
                <strong>{isoDateTimeToLabel(props.date, 'LLLL yyyy')}</strong>
              ) : (
                <strong>{props.since && isoDateTimeToLabel(props.since, 'LLLL yyyy')}</strong>
              )}
              {': '}
              <strong>
                {props.type === 'RESERVE' && t('general_end')} -{' '}
                {props.extProduct ? (
                  <>
                    {props.type === 'TARGET' && (
                      <>
                        {props.targetTitle} ({t('externalProduct_source')} {props.title})
                      </>
                    )}
                    {(props.type === 'DEPOSIT' || props.type === 'PROPERTY') && (
                      <>
                        {t('externalProduct_transferFrom')} {props.title}
                      </>
                    )}
                  </>
                ) : (
                  props.title
                )}
              </strong>
            </>
          )}
          {props.type === 'RESERVE' && (
            <>
              <br />
              {t('finQuestionnary_reserve_reserveValue')}:{' '}
              <strong>{props.value && formatAmountValue(props.value.value, props.currency)}</strong>
              {' | '}
              {t('general_portfolio')}
              {': '}
              <strong>{t(`finQuestionnary_reserve_bucket_${props.bucket}`)}</strong>
              <br />
              {!props.until && t(`options_horizon_${props.horizon}`)}
              {props.until && (
                <>
                  {` ${t('general_Until')}: `}
                  <strong>{isoDateTimeToLabel(props.until, 'LL/yyyy')}</strong>
                </>
              )}
            </>
          )}
          {props.rent && rentData && (
            <>
              <br />
              {t([
                `calculationMode_rent_allowance_${rentData.portfolioRentCalculationMode}`,
                `calculationMode_rent_${rentData.portfolioRentCalculationMode}`,
              ])}{' '}
              <strong>
                {rentData.portfolioRentCalculationMode === CalculationMode.CALCULATED &&
                  formatAmountValue(rentData.calculatedPortfolioRent.value, props.currency)}
                {rentData.portfolioRentCalculationMode === CalculationMode.MANUAL &&
                  formatAmountValue(rentData.plannedPortfolioRent.value, props.currency)}
                {rentData.portfolioRentCalculationMode === CalculationMode.REQUIRED &&
                  rentData.requiredPortfolioRent &&
                  formatAmountValue(rentData.requiredPortfolioRent.value, props.currency)}
              </strong>{' '}
              {t('general_frequency_MONTHLY')}
              <br />
              {rentData.durabilityUntil ? (
                <>
                  {` ${t('general_Until')}: `}
                  {props.personType === PersonType.PHYSICAL ? (
                    <>
                      <strong>
                        {rentData.durability.years}
                        {rentData.partnerDurability && `/${rentData.partnerDurability.years}`} {t('general_years')}
                      </strong>
                      &nbsp;
                      <span>
                        (
                        {`${t('client.age').toLocaleLowerCase()}${
                          rentData.partnerDurability ? `/${t('general_partners')}` : ''
                        }`}
                        )
                      </span>
                    </>
                  ) : (
                    <strong> {sqlDateToLabel(rentData.durabilityUntil)}</strong>
                  )}
                </>
              ) : (
                <strong> {t(`general_allTime${props.personType === PersonType.LEGAL ? '_legal' : ''}`)}</strong>
              )}
            </>
          )}
          {(props.type === 'TARGET' || props.type === 'DEPOSIT' || props.type === 'PROPERTY') && props.value && (
            <>
              <br />
              {props.type === 'TARGET' ? t('additionalTargets_value') : t('deposits_value')}:{' '}
              <strong>
                {formatAmount(props.inflatedValue ? { value: props.inflatedValue } : props.value, props.currency)}
              </strong>{' '}
              {props.frequency === TargetFrequency.REPEAT_EVERY && props.repeatYears ? (
                <>
                  {' '}
                  {t('additionalTargets_every')} {props.repeatYears}
                  {' '}
                  {pluralize(props.repeatYears, t('general_repeatYear'), t('general_repeatYears'))}
                </>
              ) : (
                t([`general_frequency_${props.frequency}`, `externalProduct_freq_${props.frequency}`])
              )}
              {props.inflatedValue && props.inflation && (
                <>
                  <br />
                  {t('general_inflationPlannedValue', { value: fractionToPercent(props.inflation.rate) })}
                </>
              )}
              <br />
              {props.frequency && props.frequency !== TargetFrequency.SINGLE && (
                <>
                  {t('general_Since')}: <strong>{props.since && isoDateTimeToLabel(props.since, 'LL/yyyy')}</strong>
                  {props.until && (
                    <>
                      {' '}
                      {t('general_Until')}: <strong>{isoDateTimeToLabel(props.until, 'LL/yyyy')}</strong>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </span>
      )}
    </span>
  );
};

export default EventLabel;
